
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface IoTGatewaySummaryStatsProps {
  totalGateways: number;
  healthyGateways: number;
  warningGateways: number;
  criticalGateways: number;
  selectedFilter: string | null;
  onFilterSelect: (filter: string | null) => void;
}

const IoTGatewaySummaryStats: React.FC<IoTGatewaySummaryStatsProps> = ({
  totalGateways,
  healthyGateways,
  warningGateways,
  criticalGateways,
  selectedFilter,
  onFilterSelect
}) => {
  const healthPercentage = totalGateways > 0 ? (healthyGateways / totalGateways) * 100 : 0;

  // Helper function to handle card click
  const handleCardClick = (filter: string) => {
    // If the current filter is already selected, clear it. Otherwise, set the new filter
    if (selectedFilter === filter) {
      onFilterSelect(null);
    } else {
      onFilterSelect(filter);
    }
  };

  // Helper function to determine if a card is selected
  const isCardSelected = (filter: string) => selectedFilter === filter;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <Card 
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          isCardSelected('all') ? "ring-2 ring-primary ring-offset-2" : ""
        )}
        onClick={() => handleCardClick('all')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Overall Health</p>
              <h3 className="text-2xl font-bold mt-1">{healthPercentage.toFixed(1)}%</h3>
              <p className="text-xs text-muted-foreground mt-1">Total: {totalGateways} devices</p>
            </div>
            <div className="rounded-full bg-green-100 p-2 dark:bg-green-900/30">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <Progress 
            value={healthPercentage} 
            className="h-2 mt-4" 
          />
        </CardContent>
      </Card>
      
      <Card 
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          isCardSelected('Healthy') ? "ring-2 ring-green-500 ring-offset-2" : ""
        )}
        onClick={() => handleCardClick('Healthy')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Healthy Gateways</p>
              <h3 className="text-2xl font-bold mt-1">{healthyGateways}</h3>
            </div>
            <div className="rounded-full bg-green-100 p-2 dark:bg-green-900/30">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <Progress 
            value={totalGateways > 0 ? (healthyGateways / totalGateways) * 100 : 0} 
            className="h-2 mt-4" 
          />
        </CardContent>
      </Card>
      
      <Card 
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          isCardSelected('Warning') ? "ring-2 ring-amber-500 ring-offset-2" : ""
        )}
        onClick={() => handleCardClick('Warning')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Warning Gateways</p>
              <h3 className="text-2xl font-bold mt-1">{warningGateways}</h3>
            </div>
            <div className="rounded-full bg-amber-100 p-2 dark:bg-amber-900/30">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <Progress 
            value={totalGateways > 0 ? (warningGateways / totalGateways) * 100 : 0} 
            className="h-2 mt-4 bg-secondary" 
          />
        </CardContent>
      </Card>
      
      <Card 
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          isCardSelected('Critical') ? "ring-2 ring-red-500 ring-offset-2" : ""
        )}
        onClick={() => handleCardClick('Critical')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Critical Gateways</p>
              <h3 className="text-2xl font-bold mt-1">{criticalGateways}</h3>
            </div>
            <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/30">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <Progress 
            value={totalGateways > 0 ? (criticalGateways / totalGateways) * 100 : 0} 
            className="h-2 mt-4 bg-secondary" 
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IoTGatewaySummaryStats;
